<template>
  <div v-if="imageIndexs.length" class="py-2.5 px-5">
    <div
      v-if="showSizeHint"
      class="relative rounded-lg border border-[#BB5332]/30 bg-[#BB5332]/10 px-[20px] py-[12px] mb-2.5 text-[14px] text-[#BB5332] font-normal text-center"
    >
      上传图片宽高数值需保持一致
      <icon
        icon="close2.svg"
        class="absolute top-2 right-2 cursor-pointer"
        :width="14"
        :height="14"
        fill="red"
        @click="() => (showSizeHint = false)"
      />
    </div>
    <template v-if="imageIndexs.length <= IMAGES_COUNT_MAX">
      <!-- 图片列表 -->
      <div
        class="grid grid-cols-5 text-xs gap-y-3 gap-x-2"
        :class="{
          ' !grid-cols-1 !flex flex-col': isSourceProgressCustom
        }"
      >
        <div
          v-for="(item, i) in imageIndexs.filter((item) => !!item)"
          :key="item.index"
          :draggable="isSourceProgressCustom ? false : true"
          class="w-full"
          :style="item.hidden ? { display: 'none' } : {}"
          @dragstart="dragstart($event, i)"
          @dragenter="dragenter($event, i)"
          @dragend="dragend"
          @dragover="dragover"
        >
          <template v-if="!isSourceProgressCustom">
            <div class="flex items-center flex-col w-full">
              <ImagePicker
                :size="48"
                :icon-size="20"
                :border-color="'rgba(255, 255, 255, 0.2)'"
                :img="images[item.index]"
                :actions="[]"
                class="w-full"
                @delete="() => onDelete(item.index, 'images')"
                @change="(image:string)=>uploadSingleImage(image, item.index)"
              />
              <div
                class="text-center mt-1 break-words w-full text-sm opacity-70 relative"
                :title="item.label"
              >
                <div class="truncate">
                  <!-- 是否为空状态默认值 -->
                  <span
                    v-if="item.emptyImageType == 'empty'"
                    class="absolute right-[90%] top-0 h-[100%] flex items-center"
                  >
                    <a-tooltip
                      :title="
                        props.shape.widgetImageArray === 'time'
                          ? '未获取数据时显示“--:--”，请上传“-”'
                          : props.shape.widgetImageArray === 'date'
                          ? '未获取数据时显示“--/--”，请上传“-”'
                          : '未获取数据时显示“--”，请上传“-”'
                      "
                    >
                      <icon
                        icon="help.svg"
                        :width="14"
                        :height="14"
                        fill="none"
                      />
                    </a-tooltip>
                  </span>
                  <span> {{ item.label }}</span>
                </div>
                <div
                  v-if="item.labelSub"
                  class="truncate"
                  :title="item.labelSub"
                >
                  {{ item.labelSub }}
                </div>
              </div>
              <!-- <small v-if="item.required" class="opacity-70">（必选）</small> -->
            </div>
          </template>
          <template v-if="isSourceProgressCustom">
            <DataSourceImageArrayListPorgressImage
              :shape="shape"
              :item="item"
              :image-src="images[item.index]"
              @upload="(image:string)=>uploadSingleImage(image, item.index)"
              @delete="() => onDelete(item.index, 'images')"
            />
          </template>
        </div>
        <!-- 负号（空状态） -->
        <template v-if="negativeSignImage">
          <div class="flex items-center flex-col w-full col-end-6">
            <ImagePicker
              :size="48"
              :icon-size="20"
              :border-color="'rgba(255, 255, 255, 0.2)'"
              :img="images[negativeSignImage?.index ?? -1]"
              @delete="() => onDelete(negativeSignImage?.index ?? -1, 'images')"
              @change="(image:string)=>uploadSingleImage(image, negativeSignImage?.index??-1)"
            />
            <div
              class="text-center mt-1 break-words w-full text-sm opacity-70 relative"
              :title="'空状态'"
            >
              <div class="truncat1e">
                <!-- 是否为默认值 -->
                <span
                  class="absolute right-[95%] top-0 h-[100%] flex items-center"
                >
                  <a-tooltip>
                    <template #title>
                      <div>
                        未获取数据时显示“--”<br />( 与负号共用同一图片 )
                      </div>
                    </template>
                    <icon
                      icon="help.svg"
                      :width="14"
                      :height="14"
                      fill="none"
                    />
                  </a-tooltip>
                </span>
                <span>{{ '空状态' }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="flex justify-end text-xs mt-4">
        <a-button class="w-[136px] bg-black-active" @click="onUploadBatch">
          批量上传
        </a-button>
        <a-button class="ml-4 bg-black-active w-[136px]" @click="onDeleteAll">
          全部删除
        </a-button>
      </div>
    </template>
    <div v-else>该数据项图片较多，无法使用’普通填充‘属性，请使用数字填充。</div>
  </div>
</template>
<script lang="ts" setup>
import { useEmitterListener, useWatchfaceImageValid } from '@/hooks';
import { getImageArrayIndexs, useImageArray } from '@/hooks/use-image-array';
import { dualtimeCityList } from '@/resources/data-source';
import { ElectronApi } from '@/services/electron.api';
import { Shape } from '@/services/shape';
import { Attributes } from '@/services/shape/attributes';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { EventType } from '@/utils/emitter.utils';
import { matchFilenames, sortFilenames } from '@/utils/fs/name.utils';
import { pickFile } from '@/utils/fs/pick-file.utils';
import { useStorage } from '@vueuse/core';
import { debounce } from 'lodash-es';
import { PropType, computed, ref } from 'vue';

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
});
const editorStore = useEditorStore();
const watchStore = useWatchfaceStore();
const imageArray = useImageArray();
const { validImageSize } = useWatchfaceImageValid();

const IMAGES_COUNT_MAX = 66;

//初始化图组循环列表
const imageIndexs = computed(() => {
  return getImageArrayIndexs(props.shape, watchStore.deviceType);
});
const negativeSignImage = computed(() =>
  imageIndexs.value.find(
    (item) =>
      item.emptyImageType == 'negative' &&
      canUse('shape-image-array-empty').valid
  )
);

const images = computed(() => {
  return props.shape.getResources({
    resourceName: 'images',
    colorGroup: editorStore.themeColor?.id ?? '',
    base64: true
  });
});

const uploadSingleImage = async (realpath: string, index: number) => {
  // 校验图片尺寸与已上传图片是否一致
  await imageArray.isSameSizeBetweenImages(
    [realpath],
    props.shape,
    editorStore.themeColorId
  );
  const path = ElectronApi.path;
  await props.shape.uploadResource({
    resourceName: 'images',
    colorGroup: editorStore.themeColor?.id ?? '',
    realpath,
    index,
    dirname: path?.basename(path?.dirname(realpath))
  });
  // 上传第一张图片时，把图片信息复制到隐藏占位位置
  if (index === 0) {
    const hiddenItem = imageIndexs.value.find((item) => item.hidden);
    if (hiddenItem) {
      // 直接再调一次 uploadResource 上传同一张图片到占位位置
      await props.shape.uploadResource({
        resourceName: 'images',
        colorGroup: editorStore.themeColor?.id ?? '',
        realpath,
        index: hiddenItem.index,
        dirname: path?.basename(path?.dirname(realpath))
      });
    }
  }
};

// 点击批量上传按钮
const onUploadBatch = async () => {
  // 选择图片
  const images = await pickFile({
    extensions: ['png'],
    multi: true
  });
  if (!images?.length || images.length > IMAGES_COUNT_MAX) return;
  // 重新排序
  uploadBatch(images);
};

// 匹配星期文件名
const matchWeekdayFilenames = (images: string[]): string[] => {
  return matchFilenames(
    images,
    ['日天', '一', '二', '三', '四', '五', '六'].map((day) => ({
      label: day,
      // 包含星期中文字符串中的任意字符 则匹配
      validator: (s: string) =>
        Array.from(day).some((char: string) => s.includes(char))
    }))
  );
};

// 匹配天气文件名
const matchWeatherFilenames = (
  images: string[],
  weatherValues: DataSourceWeatherValueItem[]
): string[] => {
  const filenameTargets = weatherValues.map((i) => ({
    label: i.label
  }));
  return matchFilenames(images, filenameTargets);
};

// 匹配双时区城市文件名
const matchDualtimeCityFilenames = (images: string[]) =>
  matchFilenames(
    images,
    dualtimeCityList.map((i) => ({
      label: i.name,
      validator: (s) =>
        s.toLowerCase().includes(i.name.toLowerCase()) ||
        s.toLowerCase().includes(i.brief.toLowerCase())
    }))
  );

// 批量上传
const uploadBatch = async (selectedImages: string[]) => {
  let _images: string[] = [];
  const { weatherValueRange } = props.shape.sourceItem ?? {};
  const { sourceWeatherType = 'whole' } = props.shape.attrs;
  if (weatherValueRange?.[sourceWeatherType]?.length) {
    // 处理天气填充
    _images = matchWeatherFilenames(
      selectedImages,
      weatherValueRange?.[sourceWeatherType] ??
        ([] as DataSourceWeatherValueItem[])
    );
  } else if (
    props.shape.attrs.source &&
    ['dateWeek', 'miscdateYestardayWeak', 'miscdateTomorrowWeak'].includes(
      props.shape.attrs.source
    )
  ) {
    // 处理星期填充
    _images = matchWeekdayFilenames(selectedImages);
  } else if (props.shape.attrs.source === 'dualtimeCity') {
    // 处理双时区城市填充
    _images = matchDualtimeCityFilenames(selectedImages);
  } else _images = sortFilenames(selectedImages);

  // 验证图片格式和大小，此时图片大小一致，只需验证最大的即可
  const res2 = await validImageSize(_images?.[0]);
  if (!res2) return;

  // 校验选择的图片大小是否一致
  await imageArray.isSameSizeBetweenImages(
    _images.map((i) => (i ? i : '')),
    props.shape,
    editorStore.themeColorId,
    true
  );

  // 批量上传图片
  const hiddenItem = imageIndexs.value.find((item) => item.hidden);
  // 截取用户实际需要上传的数量（排除隐藏占位项）
  const visibleCount = imageIndexs.value.filter((item) => !item.hidden).length;
  const realpaths = _images.slice(0, visibleCount);
  if (!realpaths.length) return;
  // 如果存在隐藏占位项，在对应位置插入第一张图片
  if (hiddenItem && realpaths[0]) {
    realpaths.splice(hiddenItem.index, 0, realpaths[0]);
  }
  if (!realpaths.length) return;
  const path = ElectronApi.path;
  const dirname = path?.basename(path?.dirname(realpaths?.[0]));
  await props.shape.uploadResources({
    resourceName: 'images',
    colorGroup: editorStore.themeColor?.id ?? '',
    realpaths,
    dirname
  });
};

// 切换类型后，删除多余的图片
useEmitterListener(
  [EventType['shape:set-attr']],
  debounce((data: { shapeId?: string; attrs?: (keyof Attributes)[] } = {}) => {
    const shape = Shape.getShape(data.shapeId ?? '');
    if (!shape) return;

    const _keys = [
      'source',
      'sourceProgressAccuracy',
      'fillType',
      'sourceWeatherType'
    ];
    const keys = [...(data?.attrs ?? [])];
    if (keys.some((key) => _keys.includes(key))) {
      const len1 = imageIndexs.value.length;
      props.shape.matchResourceLength({ resourceName: 'images', length: len1 });
    }
  }, 50)
);

// 监听属性面板拖入图片事件 批量上传
useEmitterListener(EventType['attrs:drop-images'], (files: File[]) => {
  // 拖拽时，仅考虑拖拽多个图片
  if (files.length) uploadBatch(files.map((i) => i.path));
});

// 全部删除
const onDeleteAll = () => {
  props.shape.deleteResource({
    resourceName: 'images',
    colorGroup: editorStore.themeColor?.id ?? ''
  });
  props.shape.setAttrs({ w: 100, h: 100 });
};
const onDelete = (index: number, resourceName: 'images') => {
  props.shape.deleteResource({
    resourceName: resourceName,
    colorGroup: editorStore.themeColor?.id ?? '',
    index
  });
};

// 拖拽移动
let dragIndex = 0;
const draggingIndex = ref<number>();
const dropIndex = ref<number>();
function dragstart(e: Event, index: number) {
  e.stopPropagation();
  dragIndex = index;
  draggingIndex.value = index;
  setTimeout(() => {
    (e.target as HTMLElement).classList.add('moveing');
  }, 0);
}
function dragenter(e: Event, index: number) {
  e.preventDefault();
  // 拖拽到原位置时不触发
  if (dragIndex !== index) {
    dropIndex.value = index;
    const source = imageIndexs.value[dragIndex];
    imageIndexs.value.splice(dragIndex, 1);
    imageIndexs.value.splice(index, 0, source);
    // 更新节点位置
    dragIndex = index;
  }
}
function dragover(e: DragEvent) {
  e.preventDefault();
  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = 'move';
  }
}

const dragend = async (e: DragEvent) => {
  (e.target as HTMLElement).classList.remove('moveing');
  // 修改数据
  if (draggingIndex.value == undefined || dropIndex.value == undefined) return;
  const resources = props.shape.attrs['resources'] ?? {};
  const colorId = editorStore.themeColor?.id ?? '';
  const images = resources['images']?.[colorId];
  if (!images?.length) return;

  imageIndexs.value.forEach((item) => {
    images[item.index] = images[item.index] ?? undefined;
  });
  const [item] = images.splice(draggingIndex.value, 1);
  images.splice(dropIndex.value, 0, item);
  props.shape.setAttrs({
    resources: resources
  });
};
// 图片宽高保持一致提示
const showSizeHint = useStorage('attrs-show-image-array-size', true);

// 是否是精度自定义
const isSourceProgressCustom = computed(() => {
  const shape = props.shape;
  return (
    !!shape.sourceItem?.progressAccuracy?.length &&
    shape.attrs.sourceProgressAccuracy == 0 &&
    shape.attrs.fillType == '普通填充'
  );
});
</script>
<style scoped lang="less">
.moveing {
  opacity: 0;
}
</style>
